import { CartItem, CartItemState } from '@store/orders/orders.model';

export class CartUtils {
  private static parseRequestPayload(item: CartItem): Record<string, string> {
    try {
      return JSON.parse(item.requestPayloadFull);
    } catch {
      return {};
    }
  }

  static getCartItemApn(item: CartItem): string | undefined {
    if (item.apn != null) {
      return item.apn;
    }
    return CartUtils.parseRequestPayload(item).apn;
  }

  static getCartItemFipsCode(item: CartItem): string | undefined {
    return CartUtils.parseRequestPayload(item).fipsCode;
  }

  static getCartItemParcelId(item: CartItem): string | undefined {
    return item.parcelId ?? CartUtils.parseRequestPayload(item).parcelId;
  }

  static getCartItemUniversalParcelId(item: CartItem): string | undefined {
    return CartUtils.parseRequestPayload(item).universalParcelId;
  }

  static isMatchingCartItem(item: CartItem, featureCode: string, apn: string | undefined, fipsCode?: string): boolean {
    if (apn == null) {
      return false;
    }
    if (item.state === CartItemState.VIEW) {
      return false;
    }
    if (item.productId !== featureCode && item.skuId !== featureCode) {
      return false;
    }
    if (fipsCode != null) {
      return CartUtils.getCartItemFipsCode(item) === fipsCode && CartUtils.getCartItemApn(item) === apn;
    }
    return CartUtils.getCartItemApn(item) === apn;
  }

  static isFeatureInCart(
    cartItems: Array<CartItem> | undefined,
    featureCode: string,
    apn: string | undefined,
    fipsCode?: string
  ): boolean {
    return cartItems?.some((item) => CartUtils.isMatchingCartItem(item, featureCode, apn, fipsCode)) ?? false;
  }

  static isSameProperty(left: CartItem, right: CartItem): boolean {
    const leftUniversalParcelId = CartUtils.getCartItemUniversalParcelId(left);
    const rightUniversalParcelId = CartUtils.getCartItemUniversalParcelId(right);
    if (leftUniversalParcelId != null && rightUniversalParcelId != null) {
      return leftUniversalParcelId === rightUniversalParcelId;
    }

    const leftFipsCode = CartUtils.getCartItemFipsCode(left);
    const rightFipsCode = CartUtils.getCartItemFipsCode(right);
    const leftApn = CartUtils.getCartItemApn(left);
    const rightApn = CartUtils.getCartItemApn(right);
    if (leftFipsCode != null && rightFipsCode != null && leftApn != null && rightApn != null) {
      return leftFipsCode === rightFipsCode && leftApn === rightApn;
    }

    const leftParcelId = CartUtils.getCartItemParcelId(left);
    const rightParcelId = CartUtils.getCartItemParcelId(right);
    return leftParcelId != null && rightParcelId != null && leftParcelId === rightParcelId;
  }

  static isSameReportForProperty(left: CartItem, right: CartItem): boolean {
    const sameProduct = [left.productId, left.skuId]
      .filter(Boolean)
      .some((identifier) => identifier === right.productId || identifier === right.skuId);
    return left.state !== CartItemState.VIEW
      && right.state !== CartItemState.VIEW
      && sameProduct
      && CartUtils.isSameProperty(left, right);
  }

  static addOrReplaceReportForProperty(cartItems: Array<CartItem> | undefined, itemToAdd: CartItem): Array<CartItem> {
    return [
      ...(cartItems ?? []).filter((item) => !CartUtils.isSameReportForProperty(item, itemToAdd)),
      itemToAdd,
    ];
  }

  static addOrReplaceReportsForProperty(
    cartItems: Array<CartItem> | undefined,
    itemsToAdd: Array<CartItem>
  ): Array<CartItem> {
    return itemsToAdd.reduce(
      (items, itemToAdd) => CartUtils.addOrReplaceReportForProperty(items, itemToAdd),
      cartItems ?? []
    );
  }
}
